// CragStatsChart.js
import React, {useMemo} from 'react';
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Legend } from 'recharts';
import { CLIMBING_STYLES } from '../../utils/constants.js';
import { getAscentTypeColor } from '../../utils/gradeUtils.js';
import { calculateCragStats } from '../../utils/metrics/cragStats.js';

const CragStatsChart = ({ data, climbingStyle, selectedStat, numCrags }) => {
  
  const processedData = useMemo(() => {
    if (!data?.length) return [];
    
    const stats = calculateCragStats(data, climbingStyle);
    if (!stats?.crags?.length) return [];
    
return [...stats.crags]
  .filter(crag => {
    // Filter out crags with no data for the selected stat
    if (selectedStat === 'cleanRoutes') {
      const totalRoutes = (crag.beginnerRoutes || 0) + 
                         (crag.intermediateRoutes || 0) + 
                         (crag.experiencedRoutes || 0) + 
                         (crag.expertRoutes || 0) + 
                         (crag.eliteRoutes || 0);
      return totalRoutes > 0;
    } else if (selectedStat === 'cleanAscents') {
      const totalAscents = (crag.beginnerAscents || 0) + 
                          (crag.intermediateAscents || 0) + 
                          (crag.experiencedAscents || 0) + 
                          (crag.expertAscents || 0) + 
                          (crag.eliteAscents || 0);
      return totalAscents > 0;
    } else if (selectedStat === 'attempts') {
      return crag.attempts > 0;
    } else if (selectedStat === 'cpg') {
      return typeof crag.cpg === 'number' && crag.cpg > 0;
    }
    return true;
  })
  .sort((a, b) => {
    if (selectedStat === 'cleanRoutes') {
      const aTotal = (a.beginnerRoutes || 0) + 
                    (a.intermediateRoutes || 0) + 
                    (a.experiencedRoutes || 0) + 
                    (a.expertRoutes || 0) + 
                    (a.eliteRoutes || 0);
      const bTotal = (b.beginnerRoutes || 0) + 
                    (b.intermediateRoutes || 0) + 
                    (b.experiencedRoutes || 0) + 
                    (b.expertRoutes || 0) + 
                    (b.eliteRoutes || 0);
      // Only use CPG as tiebreaker if both crags have it
      return bTotal !== aTotal ? bTotal - aTotal : 
             (typeof a.cpg === 'number' && typeof b.cpg === 'number') ? (b.cpg - a.cpg) : 0;
    } else if (selectedStat === 'cleanAscents') {
      const aTotal = (a.beginnerAscents || 0) + 
                    (a.intermediateAscents || 0) + 
                    (a.experiencedAscents || 0) + 
                    (a.expertAscents || 0) + 
                    (a.eliteAscents || 0);
      const bTotal = (b.beginnerAscents || 0) + 
                    (b.intermediateAscents || 0) + 
                    (b.experiencedAscents || 0) + 
                    (b.expertAscents || 0) + 
                    (b.eliteAscents || 0);
      return bTotal !== aTotal ? bTotal - aTotal :
             (typeof a.cpg === 'number' && typeof b.cpg === 'number') ? (b.cpg - a.cpg) : 0;
    } else if (selectedStat === 'attempts') {
      return b.attempts !== a.attempts ? b.attempts - a.attempts :
             (typeof a.cpg === 'number' && typeof b.cpg === 'number') ? (b.cpg - a.cpg) : 0;
    }
    return (b[selectedStat] || 0) - (a[selectedStat] || 0);
  })      .slice(0, numCrags);
  }, [data, climbingStyle, selectedStat, numCrags]);

  const statOptions = [
    { value: 'cpg', label: 'Cumulative Grade' },
    { value: 'attempts', label: 'Total Attempts' },
    { value: 'cleanAscents', label: 'Clean Ascents' },
    { value: 'cleanRoutes', label: 'Clean Routes' }
  ];

  // CPG uses an exponential x-axis (linear-domain sums) so gaps between
  // adjacent grades double. All other stats use ordinary linear stacked counts.
  const isBoulder = climbingStyle === CLIMBING_STYLES.BOULDER;
  const linearToCpg = (v) => (isBoulder ? Math.log(v) : Math.log2(v));
  const cpgToLinear = (c) => (isBoulder ? Math.exp(c) : Math.pow(2, c));

  // Memoize which categories have data to control legend visibility
  const activeCategories = useMemo(() => {
    if (!processedData.length) return {};

    const suffix = selectedStat === 'cleanRoutes'
      ? 'Routes'
      : selectedStat === 'cpg'
        ? 'CpgLinear'
        : 'Ascents';
    const categories = {
      beginner: processedData.some(crag => (crag[`beginner${suffix}`] || 0) > 0),
      intermediate: processedData.some(crag => (crag[`intermediate${suffix}`] || 0) > 0),
      experienced: processedData.some(crag => (crag[`experienced${suffix}`] || 0) > 0),
      expert: processedData.some(crag => (crag[`expert${suffix}`] || 0) > 0),
      elite: processedData.some(crag => (crag[`elite${suffix}`] || 0) > 0),
      // Ascent types
      onsight: processedData.some(crag => (crag.onsight || 0) > 0),
      flash: processedData.some(crag => (crag.flash || 0) > 0),
      send: processedData.some(crag => (crag.send || 0) > 0),
      repeat: processedData.some(crag => (crag.repeat || 0) > 0),
      redpoint: processedData.some(crag => (crag.redpoint || 0) > 0),
      pinkpoint: processedData.some(crag => (crag.pinkpoint || 0) > 0),
      other: processedData.some(crag => (crag.other || 0) > 0)
    };
    return categories;
  }, [processedData, selectedStat]);

  const getGradeBreakdownBars = () => {
    const suffix = selectedStat === 'cleanRoutes'
      ? 'Routes'
      : selectedStat === 'cpg'
        ? 'CpgLinear'
        : 'Ascents';

    return (
      <>
        {activeCategories.beginner && (
          <Bar
            animationDuration={300}
            dataKey={`beginner${suffix}`}
            stackId="a"
            fill="#22c55e"
            name="Beginner"
          />
        )}
        {activeCategories.intermediate && (
          <Bar
            animationDuration={300}
            dataKey={`intermediate${suffix}`}
            stackId="a"
            fill="#fbbf24"
            name="Intermediate"
          />
        )}
        {activeCategories.experienced && (
          <Bar
            animationDuration={300}
            dataKey={`experienced${suffix}`}
            stackId="a"
            fill="#fb923c"
            name="Experienced"
          />
        )}
        {activeCategories.expert && (
          <Bar
            animationDuration={300}
            dataKey={`expert${suffix}`}
            stackId="a"
            fill="#dc2626"
            name="Expert"
          />
        )}
        {activeCategories.elite && (
          <Bar
            animationDuration={300}
            dataKey={`elite${suffix}`}
            stackId="a"
            fill="#a855f7"
            name="Elite"
          />
        )}
      </>
    );
  };

  const getAscentBars = () => {
    if (climbingStyle === CLIMBING_STYLES.BOULDER) {
      return (
        <>
          {activeCategories.onsight && (
            <Bar
              animationDuration={300}
              dataKey="onsight"
              stackId="a"
              fill={getAscentTypeColor('Onsight')}
              name="Onsight"
            />
          )}
          {activeCategories.flash && (
            <Bar
              animationDuration={300}
              dataKey="flash"
              stackId="a"
              fill={getAscentTypeColor('Flash')}
              name="Flash"
            />
          )}
          {activeCategories.send && (
            <Bar
              animationDuration={300}
              dataKey="send"
              stackId="a"
              fill={getAscentTypeColor('Send')}
              name="Send"
            />
          )}
          {activeCategories.repeat && (
            <Bar
              animationDuration={300}
              dataKey="repeat"
              stackId="a"
              fill={getAscentTypeColor('Repeat')}
              name="Repeat"
            />
          )}
          {activeCategories.other && (
            <Bar
              animationDuration={300}
              dataKey="other"
              stackId="a"
              fill={getAscentTypeColor('Other')}
              name="Attempts"
            />
          )}
        </>
      );
    }
    
    return (
      <>
        {activeCategories.onsight && (
          <Bar
            animationDuration={300}
            dataKey="onsight"
            stackId="a"
            fill={getAscentTypeColor('Onsight')}
            name="Onsight"
          />
        )}
        {activeCategories.flash && (
          <Bar
            animationDuration={300}
            dataKey="flash"
            stackId="a"
            fill={getAscentTypeColor('Flash')}
            name="Flash"
          />
        )}
        {activeCategories.redpoint && (
          <Bar
            animationDuration={300}
            dataKey="redpoint"
            stackId="a"
            fill={getAscentTypeColor('Red point')}
            name="Red point"
          />
        )}
        {activeCategories.pinkpoint && (
          <Bar
            animationDuration={300}
            dataKey="pinkpoint"
            stackId="a"
            fill={getAscentTypeColor('Pink point')}
            name="Pink point"
          />
        )}
        {activeCategories.other && (
          <Bar
            animationDuration={300}
            dataKey="other"
            stackId="a"
            fill={getAscentTypeColor('Other')}
            name="Attempts"
          />
        )}
      </>
    );
  };
  
  
  const getLegendTitle = () => {
    if (selectedStat === 'attempts') {
      return 'Ascent Type';
    } else {
      return 'Difficulty';
    }
  };
  
  const CustomLegendContent = (props) => {
    const { payload } = props;
    
    const title = getLegendTitle();
    
    return (
      <div style={{ fontSize: '12px' }}>
        {title && (
          <div style={{ 
            borderBottom: '1px solid #d1d5db', 
            marginBottom: '8px', 
            paddingBottom: '4px',
            fontWeight: 500
          }}>
            {title}
          </div>
        )}
        {payload?.map((entry, index) => (
          <div key={`item-${index}`} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <div style={{ 
              width: '10px', 
              height: '10px', 
              backgroundColor: entry.color,
              marginRight: '8px'
            }}/>
            <span>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };



  // For CPG, place ticks at integer-grade boundaries (2^k or e^k) and label
  // them as the underlying grade. Start the axis one grade below the smallest
  // crag so small differences remain visible.
  const cpgAxisProps = useMemo(() => {
    if (selectedStat !== 'cpg' || !processedData.length) return null;
    const cpgs = processedData.map(c => c.cpg).filter(v => v > 0);
    if (!cpgs.length) return null;
    const minCpg = Math.floor(Math.min(...cpgs));
    const maxCpg = Math.ceil(Math.max(...cpgs));
    const ticks = [];
    for (let g = minCpg; g <= maxCpg; g++) ticks.push(cpgToLinear(g));
    return {
      domain: [cpgToLinear(minCpg - 1), cpgToLinear(maxCpg)],
      ticks,
      tickFormatter: (v) => linearToCpg(v).toFixed(0),
      allowDataOverflow: true
    };
  }, [processedData, selectedStat, isBoulder]);

  const tooltipFormatter = (value, name) => {
    if (selectedStat !== 'cpg') return [value || 0, name];
    if (!value || value <= 0) return ['—', name];
    return [linearToCpg(value).toFixed(2), `${name} (CPG-equiv)`];
  };

  return (
    <div className="w-full h-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={processedData}
          margin={{ top: 0, right: 0, left: 0, bottom: 20 }}
          layout="vertical"
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            domain={cpgAxisProps ? cpgAxisProps.domain : [0, 'auto']}
            ticks={cpgAxisProps ? cpgAxisProps.ticks : undefined}
            tickFormatter={cpgAxisProps ? cpgAxisProps.tickFormatter : undefined}
            allowDataOverflow={cpgAxisProps ? cpgAxisProps.allowDataOverflow : false}
            label={{
              value: statOptions.find(opt => opt.value === selectedStat)?.label,
              position: "bottom",
              offset: 0
            }}
          />
          <YAxis 
            type="category" 
            dataKey="name" 
            width={160}
            interval={0}
            tick={({ x, y, payload }) => (
              <text
                x={x}
                y={y}
                dy={3}
                textAnchor="end"
                fontSize={numCrags > 10 ? 10 : 12}
                fill="#666"
              >
                {payload.value}
              </text>
            )}
          />
          <Tooltip
            formatter={tooltipFormatter}
          />
          <Legend 
            content={<CustomLegendContent />}
            layout="vertical"
            align="center"
            verticalAlign="middle"
            wrapperStyle={{
              fontSize: '12px',
              padding: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              position: 'absolute',
              bottom: '55px',
              right: '5px',
              transform: 'translateY(0)',
              zIndex: 100
            }}
          />
          
          {selectedStat === 'attempts' ? (
            getAscentBars()
          ) : (
            getGradeBreakdownBars()
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CragStatsChart;